import { jsxs, jsx } from 'react/jsx-runtime';
import clsx from 'clsx';
import { forwardRef, useState, useEffect } from 'react';
import { TextFieldStatuses } from '../types/index.js';
import { useLayoutStyles, useSkinStyles } from '../styles/index.js';
import { computeResponsiveness } from '../../../../foundation/responsive/helpers/index.js';
import { ControlSizesXL } from '../../../helpers/controlSizes/controlSizes/index.js';

const TextField = forwardRef(({ type, name, size = ControlSizesXL, status, dataTestid, startIcon: StartIcon, endIcon: EndIcon, placeholder, iconClick, isDisabled, isReadOnly, isWithoutBackground, tabIndex = 0, buttonTabIndex = 0, className, enterKeyHint, isCentered, ariaLabel, isWithoutBorders, actionProps, onBlur, onChange, ...props }, ref) => {
    const sizes = computeResponsiveness(size);
    const layoutStyles = useLayoutStyles({
        sizes, startIcon: StartIcon, endIcon: EndIcon, isCentered,
    });
    const skinStyles = useSkinStyles({ isWithoutBackground, isWithoutBorders });
    const [isFilled, setIsFilled] = useState(!!props.value || !!props.defaultValue);
    useEffect(() => {
        const filled = !!props.value || props.value === 0;
        setIsFilled(filled);
    }, [props.value]);
    const handleChange = (e) => {
        setIsFilled(!!e.target.value);
        onChange?.(e);
    };
    return (jsxs("div", { className: clsx(layoutStyles.root, className), "data-testid": "text-field-root", children: [jsx("input", { enterKeyHint: enterKeyHint, className: clsx(layoutStyles.control, skinStyles.default, layoutStyles.controlDefault, {
                    [layoutStyles.controlDefault]: !StartIcon && !EndIcon,
                    [layoutStyles.controlPlaceholder]: StartIcon,
                    [layoutStyles.controlButton]: EndIcon,
                    [skinStyles.default]: !status,
                    [skinStyles.interactive]: !isFilled && !status && !isReadOnly,
                    [skinStyles.filled]: isFilled,
                    [skinStyles.error]: status === TextFieldStatuses.Error,
                    [skinStyles.warning]: status === TextFieldStatuses.Warning,
                    [skinStyles.success]: status === TextFieldStatuses.Success,
                    [skinStyles.disabled]: isDisabled,
                }), type: type, ref: ref, name: name, tabIndex: tabIndex, "data-testid": dataTestid || 'text-control-input', placeholder: placeholder, readOnly: isReadOnly, disabled: isDisabled, onChange: handleChange, onBlur: onBlur, inputMode: type === 'number' ? 'decimal' : undefined, ...props }), StartIcon && !isCentered && jsx(StartIcon, { className: clsx(layoutStyles.placeholderIcon, skinStyles.placeholderIcon), "data-testid": "placeholder-icon" }), EndIcon && !isCentered && (jsx("button", { ...actionProps, type: "button", "data-testid": "button-icon", disabled: isDisabled, onClick: iconClick, className: clsx(layoutStyles.button, skinStyles.button), tabIndex: buttonTabIndex, children: jsx(EndIcon, { className: clsx(layoutStyles.buttonIcon, skinStyles.buttonIcon), "data-testid": "end-icon", "aria-label": ariaLabel }) }))] }));
});
TextField.displayName = 'TextField';

export { TextField };
